// App

import { useState } from 'react';
import numeral from 'numeral';

// Constellation
import {
  AdornedInput,
  Box,
  Button,
  ButtonGroup,
  FieldSet,
  FormField,
  FormHelp,
  Heading,
  Label,
  Legend,
  Modal,
  Paragraph,
  TextButton,
  VisuallyHidden,
} from '@zillow/constellation-10';

// Components
import PriceCard from './PriceCard';

// Flux/Actions

import { useAppSelector } from '../flux/configureStore';

// Misc / utils
import { RentalGrowthRentEstimateSource } from '@zg-rentals/ts-rental-growth-api-client';
import { augmentError, formatForLog } from '../utils/errors';
import { logger } from '../index';

// Types
import type React from 'react';
import type AddressModelType from '../models/Address';
import type { PartialFilterType } from '../models/Types';

const ChangePriceModal = () => {
  const userProperty: AddressModelType = useAppSelector((state) => state.address);
  const priceFromFilter = useAppSelector((state) => state.filter.price);
  const setFilter = useAppSelector((state) => state.setFilter);

  const { street, unit, rentZestimate, rentZestimateSource } = userProperty;
  const isCurrentPriceCustom = !!priceFromFilter && priceFromFilter !== rentZestimate;
  const addressText = `${street} ${unit && unit !== ' ' ? ', Unit ' + unit : ''}`;
  const selectableRentZestimate =
    rentZestimateSource === RentalGrowthRentEstimateSource.LISTING_STATS ? null : rentZestimate;

  const [activeCard, updateactiveCard] = useState(
    isCurrentPriceCustom ? 'custom' : selectableRentZestimate ? 'zestimate' : 'custom',
  );

  const [inputPrice, updateInputPrice] = useState(isCurrentPriceCustom ? numeral(priceFromFilter).format('0,0') : '');
  const [errorMsg, updateErrorMsg] = useState('');

  const handleClickZestimate = () => {
    updateErrorMsg('');
    updateactiveCard('zestimate');
  };

  const handleClickCustom = () => {
    updateactiveCard('custom');
  };

  const handlePriceChange = (e: React.FormEvent<HTMLInputElement>) => {
    // @ts-expect-error https://github.com/desktop/desktop/issues/297
    const enteredValue = e.target.value;

    updateErrorMsg('');
    updateInputPrice(enteredValue ? numeral(enteredValue).format('0,0') : '');
  };

  const handleKeyDown = (e: React.FormEvent<HTMLInputElement>) => {
    // @ts-expect-error https://github.com/desktop/desktop/issues/297
    if (e.key === 'Enter') {
      // prevents the last used button (the Change price button) from triggering
      e.preventDefault();

      handleSave();
    }
  };

  const handleCancel = () => {
    updateErrorMsg('');
  };

  const handleSave = async () => {
    const priceInput = activeCard === 'zestimate' ? rentZestimate : inputPrice;
    const newPrice = numeral(priceInput).value();

    if (activeCard === 'custom' && newPrice === null) {
      updateErrorMsg('Enter an amount');
      return;
    }

    const partialFilterObj: PartialFilterType = { price: newPrice };

    try {
      await setFilter(partialFilterObj);
    } catch (err) {
      augmentError(err, {
        root: 'components/ChangePriceModal#handleSave',
      });

      logger.error(formatForLog(err), 'Filter: Error handling filter price change');
    }
  };

  return (
    <Modal
      multiModalTriggerId="change-price-modal"
      size={{ base: 'fullScreen', lg: 'sm' }}
      header={<Heading level={1}>Price</Heading>}
      onOpenChange={(open) => !open && handleCancel()}
      body={
        <Box css={{ paddingInline: '200' }}>
          <Paragraph textStyle="body-bold">{addressText}</Paragraph>
          <Paragraph css={{ marginTop: '400', marginBottom: '600' }}>
            {selectableRentZestimate ? (
              <>
                We&#39;ll show you nearby rental properties closest to the price you choose. You can compare prices
                based on the Rent Zestimate®, or enter a custom price.
              </>
            ) : (
              <>Enter a custom price and we&#39;ll show you nearby rental listings closest to the price you choose.</>
            )}
          </Paragraph>
          {selectableRentZestimate && (
            <FieldSet
              legend={
                <VisuallyHidden asChild>
                  <Legend>Comparison price</Legend>
                </VisuallyHidden>
              }
            >
              <PriceCard
                description={`The Rent Zestimate for this property is $${numeral(selectableRentZestimate).format('0,0')}/mo`}
                handleClick={handleClickZestimate}
                isActive={activeCard === 'zestimate'}
                title="Use Rent Zestimate"
              />
              <PriceCard
                description="See competing listings at your chosen price point"
                handleClick={handleClickCustom}
                isActive={activeCard === 'custom'}
                title="Enter a Custom Price"
              />
            </FieldSet>
          )}
          {activeCard === 'custom' && (
            <>
              <FormField
                label={<Label>Your custom price</Label>}
                required
                error={errorMsg !== ''}
                control={
                  <AdornedInput
                    css={{ marginTop: '200' }}
                    startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
                    input={
                      <AdornedInput.Input
                        onChange={handlePriceChange}
                        onKeyDown={handleKeyDown}
                        value={parseInt(inputPrice, 10) === rentZestimate ? '' : inputPrice}
                      />
                    }
                    size="lg"
                  />
                }
                description={errorMsg !== '' ? <FormHelp>{errorMsg}</FormHelp> : undefined}
              />
              <Box css={{ marginTop: '200', marginBottom: '400' }} />
            </>
          )}
        </Box>
      }
      footer={
        <ButtonGroup aria-label="Set price">
          <Modal.Close>
            <TextButton>Cancel</TextButton>
          </Modal.Close>
          <Modal.Close>
            <Button tone="brand" emphasis="filled" onClick={handleSave}>
              Set price
            </Button>
          </Modal.Close>
        </ButtonGroup>
      }
    />
  );
};

export default ChangePriceModal;
